import { createMuiTheme, useMediaQuery } from "@material-ui/core";

/** Material-ui default theme, to copy from where necessary. */
// const defaults = createMuiTheme();

// #region Example to copy shadows from default theme.
/**
 * Override box shadows[3]. Must provide all 25 array elements...
 * - https://github.com/mui-org/material-ui/issues/8780
 * - https://gist.github.com/phacks/6c3c3a5f395f6e9660ae132c237250a3
 */
// const boxShadow = "0 0 4px -4px rgba(0,0,0,0.12), 0 0 8px 0 rgba(0,0,0,0.16)";
// const shadows = [...defaults.shadows];
// shadows[3] = boxShadow;
// #endregion

/**
 * Soft elevation ramp, replacing MUI's default shadows app-wide.
 *
 * The stock ones put a near-opaque 1px band directly under the element, which
 * reads as a dark blocky edge. These are a tight contact shadow plus a wider
 * ambient one, both low opacity — the look already used by the Records tab
 * bar. MUI needs all 25 entries or elevation lookups fall off the end.
 */
const softShadow = (e) => {
  const y = Math.max(1, Math.round(e * 0.7));
  const blur = Math.round(e * 2) + 3;
  // Opacity climbs with elevation: 6% is right for a panel sitting on the page
  // but would leave a menu or dialog floating with no separation from it.
  const ambient = Math.min(0.14, 0.06 + e * 0.004).toFixed(3);
  const contact = Math.min(0.1, 0.04 + e * 0.003).toFixed(3);
  return (
    `0px ${y}px ${blur}px rgba(15,23,42,${ambient}), ` +
    `0px ${Math.max(1, Math.round(y / 2))}px ${Math.round(blur / 2)}px rgba(15,23,42,${contact})`
  );
};

export const softShadows = [
  "none",
  ...Array.from({ length: 24 }, (_, i) => softShadow(i + 1)),
];

/** Border that pairs with the soft shadows — panels need an edge, not weight. */
export const softBorder = "1px solid rgba(0,0,0,0.10)";

/**
 * Default theme for the app based on the
 * [Materiaul UI default theme](https://material-ui.com/customization/default-theme/)
 * with example overrides.
 */
export const defaultTheme = createMuiTheme({
  palette: {
    type: "light",
    primary: { main: "#315179", contrastText: "#fff" },
    secondary: {
      main: "#C8C8C8",
      contrastText: "#000",
      dark: "#eef4fa",
      light: "#fff",
    },
    success: { main: "#4CAF50", contrastText: "#fff" },
    info: { main: "#315179", contrastText: "#fff" },
    warning: { main: "#ffb400", contrastText: "#000" },
    error: { main: "#ff0000", contrastText: "#fff" },
  },
  overrides: {
    MuiFormControlLabel: {
      label: {
        fontSize: "12px",
      },
    },
    MuiAutocomplete: {
      option: {
        '&[data-focus="true"]': {
          backgroundColor: "#E3F2FD",
          color: "#315179",
          fontSize: 12,
        },
        fontSize: 12,
      },
    },
    MuiInputBase: {
      root: {
        borderRadius: 3,
        fontSize: "0.875rem",
      },
      input: {
        fontSize: "0.875rem",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 3,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 3,
        fontSize: '13px'
      },
    },
    MuiAccordion: {
      root: {
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04)",
        "&$expanded": {
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 3,
      },
    },
    MuiChip: {
      root: {
        borderRadius: 3,
      },
    },
  },
  // #region Example global overrides for this theme:
  // overrides: {
  //   // Example to override "contained" variant of the Material-ui Button:
  //   // MuiButton: {
  //   //   contained: {
  //   //     boxShadow: "none",
  //   //     "&:hover": {
  //   //       boxShadow: "none",
  //   //     },
  //   //   },
  //   // },

  //   // Example to override the "contained" variant of Material-ui ButtonGroup:
  //   // MuiButtonGroup: {
  //   //   contained: {
  //   //     boxShadow: "none",
  //   //     border: "1px solid rgba(0,0,0,0.16)",
  //   //   },
  //   // },

  //   // Example to override the paper style of the Material-ui Menu:
  //   // MuiMenu: {
  //   //   paper: {
  //   //     boxShadow,
  //   //   },
  //   // },

  // },
  // #endregion

  // #region Example default props for any Material-ui components rendered with
  // this theme:
  props: {
    MuiCheckbox: {
      color: "primary",
      disableRipple: true,
    },
    MuiTextField: {
      variant: "outlined",
      size: "small",
      InputLabelProps: {
        shrink: true,
      },
    },
  },
  // #endregion

  // Everything that derives a corner from the theme rather than declaring one
  // — the outlined Select's inner element, filled inputs, menus — picks this
  // up, so inputs match the 3px used on buttons and panels.
  shape: { borderRadius: 3 },
  shadows: softShadows,
});

/**
 * Returns `true` if the screen is sized for mobile. Depends on theme
 * breakpoints, otherwise this function would be defined in `lib/hooks.js`
 */
export function useMobile(size = "xs") {
  return useMediaQuery(defaultTheme.breakpoints.down(size));
}
